import {useCallback, useEffect, useRef, useState} from 'react';

import {getCurrentEnvironment} from 'src/openapi/admin/apis/ClientConfigApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import type {GetCurrentEnvironment$Config} from 'src/openapi/admin/apis/ClientConfigApi';
import type {StringJson} from 'src/openapi/admin/models/StringJson';


type LoadEnvironmentResult = {
    success: true,
    data: StringJson,
} | {
    success: false,
    error: string,
};

export const useGetCurrentEnvironment = () => {
    const apiDispatch = useAppDispatch();
    const inFlightRef = useRef(false);
    const {createAbortSignal} = useAbortSignal();

    const [environment, setEnvironment] = useState<StringJson | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadEnvironment = useCallback(async (
        config?: GetCurrentEnvironment$Config,
    ): Promise<LoadEnvironmentResult | undefined> => {
        if (inFlightRef.current) {
            return undefined;
        }

        inFlightRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            const action = await apiDispatch(getCurrentEnvironment(config));

            if (action.payload.success) {
                setEnvironment(action.payload.data);
                return {success: true, data: action.payload.data};
            }

            const errorMessage = action.payload.data
                ? getError({error: action.payload.data})
                : 'Failed to load current environment.';

            setError(errorMessage);
            return {success: false, error: errorMessage};
        } catch {
            const errorMessage = 'Failed to load current environment.';
            setError(errorMessage);
            return {success: false, error: errorMessage};
        } finally {
            setIsLoading(false);
            inFlightRef.current = false;
        }
    }, [apiDispatch]);


    useEffect(() => {
        const signal = createAbortSignal();
        loadEnvironment({signal}).catch(() => undefined);
    }, [loadEnvironment, createAbortSignal]);

    return {
        environmentValue: environment?.value ?? null,
        error,
        isLoading,
        loadEnvironment,
    };
};
